import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { DSSetup, Service } from 'webapp-types';
import type { BlockVisualization } from 'webapp/enums/enums';

import type Block from 'webapp/models-custom/block';
import type Board from 'webapp/models-custom/board';
import type BoardState from 'webapp/models-custom/board-state';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';
import type NoRerenderSortable from 'webapp/models-custom/no-rerender-sortable';
import type BlockRecord from 'webapp/models/block';

import type { EmptyBlockSetup, NewBlockData } from 'webapp/services/model-manager/blocks';

export interface BoardBlocksBaseArgs {
	boardState: BoardState;
	currentZoom: number;
	isViewOnly: boolean;
	skipBlockFetchLoaders: boolean;
	skipActions?: boolean;
}

export default class BoardBlocksBaseComponent<T extends BoardBlocksBaseArgs> extends Component<T> {
	@service declare analytics: Service.Analytics;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service declare logger: Service.Logger;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('tour') declare tourService: Service.Tour;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked activeDatawall?: DatawallGrid;

	noRerenderSortable!: NoRerenderSortable<Block>;

	el!: JQuery;

	get board() {
		return this.args.boardState.board as Board;
	}

	get blocks() {
		return this.board.blockObjs;
	}

	get canEditName(): boolean {
		return !this.args.isViewOnly;
	}

	get colorConfig() {
		return this.board.colorConfig;
	}

	get showPlaceholder(): boolean {
		return !this.args.isViewOnly && this.blocks.length === 0;
	}

	@cached
	get databoardContent(): { title: string; text: string; icon: IconName } {
		let databoardContent: { title: string; text: string; icon: IconName };
		if (this.canEditName) {
			databoardContent = {
				title: 'Getting started',
				text: 'To get started, drag & drop datablocks to this screen from the left panel.',
				icon: 'DragAndDrop',
			};
		} else {
			databoardContent = {
				title: 'Empty dashboard',
				text: 'You need to add a block first.',
				icon: 'FileText',
			};
		}
		return databoardContent;
	}

	// ------ START NO-RERENDER LOGIC -------
	@action
	updateModel(forceSet = false): void {
		this.noRerenderSortable?.updateModel(forceSet);
	}

	// ------ END NO-RERENDER LOGIC	---------

	addNewBlock(
		newBlockData: NewBlockData,
		blockSetupId?: EmptyBlockSetup,
		placeholderCt?: JQuery,
	): void {
		const board = this.board,
			isNewBoard = board.isNew;

		if (this.mmBoardsService.handleFullBoard(board)) {
			return;
		}

		this.mmBlocksService
			.addBlockToBoardInDesigner(board, newBlockData, blockSetupId, this.activeDatawall)
			.then((blockRecord: BlockRecord) => {
				const blockId = blockRecord.id;
				schedule('afterRender', this, () => {
					// redirect first to the board, and then to block
					// otherwise, in case of a new board, routes/designer/board:beforeModel will cleanup / deselect the block
					const newRoute = `/layout-designer/${blockRecord.boardId}`;
					this.navigationService.replaceRoute(newRoute).then(() => {
						const checkToOpenAiSummaryAutocreatePopupFn = () => {
							if (isNewBoard) {
								this.mmBlocksService.checkToOpenAiSummaryAutocreatePopup(
									board,
									this.activeDatawall,
								);
							}
						};
						if (blockId) {
							this.navigationService
								.replaceRoute(`${newRoute}/${blockId}`)
								.then(checkToOpenAiSummaryAutocreatePopupFn);
						} else {
							checkToOpenAiSummaryAutocreatePopupFn();
						}
					});
				});
			})
			.finally(() => {
				placeholderCt?.remove();
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	protected getBlockDataFromDropEvent(uiItem: JQuery<HTMLElement>): Promise<NewBlockData> {
		const blockData: NewBlockData = {
			visualization: uiItem.data('vis') as BlockVisualization,
		};

		// attr, not data(): data() would turn a numeric-looking key (a custom push metric can be called 2024) into a number
		const metricSetupKey = uiItem.attr('data-metricsetupkey'),
			spaceAccessId = +uiItem.data('metricsetupspaceaccessid') || null;

		// only used when there is a key
		let resolvePromise = Promise.resolve({
			key: metricSetupKey,
			spaceAccessId,
		} as DSSetup.MetricSetupData);
		if (metricSetupKey && spaceAccessId) {
			resolvePromise = this.mmMetricsService.resolveGenericMetricWithSpecific(
				metricSetupKey,
				spaceAccessId,
			);
		}
		return resolvePromise.then((metricSetupData: DSSetup.MetricSetupData) => {
			if (metricSetupKey) {
				blockData.fromMetricSetup = metricSetupData;
			}
			return blockData;
		});
	}

	@action
	setupView(node: HTMLElement): void {
		this.el = $(node);
		this.noRerenderSortable.ct = this.el.find('.blocks');
	}
}
